<template>
  <div class="range-slider">
    <label class="range-slider__legend">
      {{ label }}
      <span class="unit" v-if="unit"> ({{ unit }})</span>
    </label>

    <div class="range-slider__wrapper">
      <vue-slider
        v-model="internal"
        :min="minValue"
        :max="maxValue"
        :step="steps"
        :dot-num="2"
        :direction="'ltr'"
        :tooltip="'always'"
        :process-style="processStyle"
        :dot-style="dotStyle">
      </vue-slider>
    </div>
  </div>
</template>

<script>
import VueSlider from 'vue-3-slider-component';

export default {
  name: 'RangeSlider',
  components: { VueSlider },
  props: {
    label: String,
    modelValue: { type: Object, default: () => ({}) },
    minValue: { type: Number, required: true },
    maxValue: { type: Number, required: true },
    unit: String,
    steps: { type: Number, default: 1 },
    emitDebounceMs: { type: Number, default: 250 }
  },
  emits: ['update:modelValue'],
  data() {
    const mv = this.modelValue || {};
    const min = (typeof mv.min === 'number') ? mv.min : this.minValue;
    const max = (typeof mv.max === 'number') ? mv.max : this.maxValue;
    return {
      internal: [min, max],
      isMinDisabled: !!mv.minDisabled,
      isMaxDisabled: !!mv.maxDisabled,
      _emitTimeout: null
    };
  },
  computed: {
    displayMin() {
      return this.isMinDisabled ? '—' : this.internal[0];
    },
    displayMax() {
      return this.isMaxDisabled ? '—' : this.internal[1];
    },
    processStyle() {
      return { backgroundColor: '#090909' };
    },
    dotStyle() {
      return { backgroundColor: '#00e2b0', border: '1px solid #090909' };
    },
    leftMinPercent() {
      const range = this.maxValue - this.minValue;
      if (range <= 0) return 0;
      const pct = ((this.internal[0] - this.minValue) / range) * 100;
      return Math.min(100, Math.max(0, pct));
    },
    leftMaxPercent() {
      const range = this.maxValue - this.minValue;
      if (range <= 0) return 100;
      const pct = ((this.internal[1] - this.minValue) / range) * 100;
      return Math.min(100, Math.max(0, pct));
    }
  },
  watch: {
    modelValue: {
      handler(newVal) {
        if (!newVal) return;
        if (typeof newVal.min === 'number') this.internal[0] = newVal.min; else this.internal[0] = this.minValue;
        if (typeof newVal.max === 'number') this.internal[1] = newVal.max; else this.internal[1] = this.maxValue;
        this.isMinDisabled = !!newVal.minDisabled;
        this.isMaxDisabled = !!newVal.maxDisabled;
      },
      deep: true
    },
    internal: {
      handler() {
        this.emitValue();
      },
      deep: true
    }
  },
  methods: {
    handleDotClick(index) {
      if (index === 0) {
        this.isMinDisabled = !this.isMinDisabled;
      } else if (index === 1) {
        this.isMaxDisabled = !this.isMaxDisabled;
      }
      // emit immediately when toggling disabled flags
      this.emitValue(true);
    },
    emitValue(immediate = false) {
      const doEmit = () => {
        const payload = {};
        if (!this.isMinDisabled && this.internal[0] !== this.minValue) payload.min = this.internal[0];
        if (!this.isMaxDisabled && this.internal[1] !== this.maxValue) payload.max = this.internal[1];
        if (this.isMinDisabled) payload.minDisabled = true;
        if (this.isMaxDisabled) payload.maxDisabled = true;
        this.$emit('update:modelValue', payload);
        this._emitTimeout = null;
      };

      if (immediate) {
        if (this._emitTimeout) {
          clearTimeout(this._emitTimeout);
          this._emitTimeout = null;
        }
        doEmit();
        return;
      }

      if (this._emitTimeout) clearTimeout(this._emitTimeout);
      this._emitTimeout = setTimeout(() => doEmit(), this.emitDebounceMs);
    }
  },
  beforeUnmount() {
    if (this._emitTimeout) {
      clearTimeout(this._emitTimeout);
      this._emitTimeout = null;
    }
  }
};
</script>

<style scoped>
.range-slider .vue-slider {
  max-width: calc(100% - 40px);
  min-width: calc(100% - 40px);
  margin: 16px 8px 0;
}
.range-slider__wrapper {
  position: relative;
}
.range-slider__values {
  display: flex;
  justify-content: space-between;
  margin-top: 12px;
}
.dot {
  position: absolute;
  top: 0;
  transform: translate(-50%, -140%);
  display: inline-block;
  padding: 4px 8px;
  background: #eee;
  border-radius: 4px;
  cursor: pointer;
  border: none;
  white-space: nowrap;
}
.dot.disabled { opacity: 0.5; }
</style>